import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  Avatar,
} from '@mui/material';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import { useParams, useNavigate } from 'react-router-dom';
import AudioPlayer from '../../../components/common/AudioPlayer';
import EmptyCallCard from '../../../components/common/EmptyCallCard';
import PhoneIcon from '@mui/icons-material/Phone';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMissedIcon from '@mui/icons-material/CallMissed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NotesIcon from '@mui/icons-material/Notes';
import {
  getInitials,
  getAvatarColorFromString,
  getTopicTagChipColors,
} from '../../../utils/helper';
import { LeadTabPanelLoader, LeadTabInlineProgress } from './components/LeadTabPanelLoader';
import { useLeadTabInfiniteScroll } from './components/useLeadTabInfiniteScroll';

/* ─────────────────────────── helpers ─────────────────────────── */

/** Icon size aligned with lead tab filter chips (~10–12px visual weight). */
const CALL_TYPE_ICON_SX = { fontSize: 12 };

const callTypeConfig = {
  outgoing: {
    icon: <CallMadeIcon sx={CALL_TYPE_ICON_SX} />,
    color: '#10B981',
    bg: '#F0FDF4',
    border: '#10B98130',
    label: 'Outgoing',
  },
  incoming: {
    icon: <CallReceivedIcon sx={CALL_TYPE_ICON_SX} />,
    color: '#3B82F6',
    bg: '#EFF6FF',
    border: '#3B82F630',
    label: 'Incoming',
  },
  missed: {
    icon: <CallMissedIcon sx={CALL_TYPE_ICON_SX} />,
    color: '#EF4444',
    bg: '#FEF2F2',
    border: '#EF444430',
    label: 'Missed',
  },
  default: {
    icon: <PhoneIcon sx={CALL_TYPE_ICON_SX} />,
    color: '#64748B',
    bg: '#F8FAFC',
    border: '#E2E8F0',
    label: 'Call',
  },
};

const getCallType = (callType) =>
  callTypeConfig[(callType || '').toLowerCase()] || callTypeConfig.default;

/** Asset disposition: MSD = Missed, NC = Not Connected (see CALL_DISPOSITION_CHOICES). */
const normalizeCallType = (t) => (t || '').toLowerCase();

const callMatchesFilterTab = (call, tabId) => {
  if (tabId === 'all') return true;
  const ct = normalizeCallType(call.callType);
  const st = String(call.status || '').toUpperCase();
  if (tabId === 'incoming') return ct === 'incoming';
  if (tabId === 'outgoing') return ct === 'outgoing';
  if (tabId === 'missed') return st === 'MSD' || ct === 'missed';
  if (tabId === 'not_connected') return st === 'NC';
  return true;
};

const CALL_FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'incoming', label: 'Incoming' },
  { id: 'outgoing', label: 'Outgoing' },
  { id: 'missed', label: 'Missed' },
  { id: 'not_connected', label: 'Not connected' },
];

/** Per-tab colors aligned with call direction / disposition (summary bar palette). */
const FILTER_TAB_THEME = {
  all: {
    accent: '#0F172A',
    border: '#E2E8F0',
    borderActive: '#334155',
    bg: '#FFFFFF',
    bgActive: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
    label: '#475569',
    labelActive: '#0F172A',
    badgeBg: '#F1F5F9',
    badgeBgActive: '#E2E8F0',
    badgeColor: '#64748B',
    badgeColorActive: '#0F172A',
    shadow: '0 1px 4px rgba(15,23,42,0.12)',
  },
  incoming: {
    accent: '#3B82F6',
    border: '#BFDBFE',
    borderActive: '#3B82F6',
    bg: '#EFF6FF',
    bgActive: 'linear-gradient(180deg, #DBEAFE 0%, #BFDBFE 100%)',
    label: '#1D4ED8',
    labelActive: '#1E3A8A',
    badgeBg: '#DBEAFE',
    badgeBgActive: '#93C5FD',
    badgeColor: '#2563EB',
    badgeColorActive: '#1E3A8A',
    shadow: '0 1px 4px rgba(59,130,246,0.25)',
  },
  outgoing: {
    accent: '#10B981',
    border: '#A7F3D0',
    borderActive: '#10B981',
    bg: '#F0FDF4',
    bgActive: 'linear-gradient(180deg, #D1FAE5 0%, #A7F3D0 100%)',
    label: '#047857',
    labelActive: '#065F46',
    badgeBg: '#D1FAE5',
    badgeBgActive: '#6EE7B7',
    badgeColor: '#059669',
    badgeColorActive: '#064E3B',
    shadow: '0 1px 4px rgba(16,185,129,0.25)',
  },
  missed: {
    accent: '#EF4444',
    border: '#FECACA',
    borderActive: '#EF4444',
    bg: '#FEF2F2',
    bgActive: 'linear-gradient(180deg, #FEE2E2 0%, #FECACA 100%)',
    label: '#B91C1C',
    labelActive: '#991B1B',
    badgeBg: '#FEE2E2',
    badgeBgActive: '#FCA5A5',
    badgeColor: '#DC2626',
    badgeColorActive: '#7F1D1D',
    shadow: '0 1px 4px rgba(239,68,68,0.25)',
  },
  not_connected: {
    accent: '#D97706',
    border: '#FDE68A',
    borderActive: '#D97706',
    bg: '#FFFBEB',
    bgActive: 'linear-gradient(180deg, #FEF3C7 0%, #FDE68A 100%)',
    label: '#B45309',
    labelActive: '#92400E',
    badgeBg: '#FEF3C7',
    badgeBgActive: '#FCD34D',
    badgeColor: '#D97706',
    badgeColorActive: '#78350F',
    shadow: '0 1px 4px rgba(217,119,6,0.22)',
  },
};

/* ─────────────────────────── Call filter tabs ─────────────────────────── */

const CallFilterTabs = ({ activeTab, onChange, counts }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mb: 2,
      flexWrap: 'wrap',
      rowGap: 0.75,
    }}
  >
    {CALL_FILTER_TABS.map((tab) => {
      const n = counts[tab.id] ?? 0;
      const active = activeTab === tab.id;
      const th = FILTER_TAB_THEME[tab.id] || FILTER_TAB_THEME.all;
      return (
        <Box
          key={tab.id}
          component="button"
          type="button"
          onClick={() => onChange(tab.id)}
          sx={{
            appearance: 'none',
            border: active ? `1.5px solid ${th.borderActive}` : `1px solid ${th.border}`,
            background: active ? th.bgActive : th.bg,
            color: active ? th.labelActive : th.label,
            borderRadius: '999px',
            px: 1.1,
            py: 0.35,
            fontSize: '0.625rem',
            fontWeight: active ? 800 : 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.35,
            transition: 'background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s',
            boxShadow: active ? th.shadow : 'none',
            '&:hover': {
              borderColor: th.borderActive,
              background: active ? undefined : th.bgActive,
              color: th.labelActive,
              boxShadow: active ? th.shadow : '0 1px 3px rgba(0,0,0,0.06)',
            },
          }}
        >
          {tab.label}
          <Box
            component="span"
            sx={{
              minWidth: 16,
              height: 16,
              px: 0.3,
              borderRadius: '99px',
              fontSize: '0.55rem',
              fontWeight: 800,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: active ? th.badgeBgActive : th.badgeBg,
              color: active ? th.badgeColorActive : th.badgeColor,
            }}
          >
            {n}
          </Box>
        </Box>
      );
    })}
  </Box>
);

/* ─────────────────────────── CallCard ─────────────────────────── */

const CallCard = ({
  id,
  date,
  duration,
  summary,
  topics,
  agent,
  recordingUrl,
  durationFormatted,
  onViewDetails,
  callType,
  index,
}) => {
  const navigate = useNavigate();
  const ct = getCallType(callType);
  const hasSummary = summary && summary !== 'No summary available';
  const hasRecording = !!recordingUrl;
  const avatarSeed = String(agent?.id ?? agent?.name ?? `call-${id ?? index}`);
  const avatarBg = getAvatarColorFromString(avatarSeed);
  const initials = agent?.name ? getInitials(agent.name) : '';

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: '14px',
        background: '#FFFFFF',
        border: '0.5px solid',
        borderColor: 'transparent',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, transform 0.15s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
          transform: 'translateY(-1px)',
        },
        '@keyframes fadeUp': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'fadeUp 0.3s ease both',
        animationDelay: `${index * 70}ms`,
      }}
    >
      <Box sx={{ pl: 2.5, pr: 2, pt: 2, pb: 2 }}>

        {/* ── Top row: avatar + agent + call type chip + date ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>

          {/* Avatar */}
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: avatarBg,
              color: '#FFFFFF',
              border: `1.5px solid ${avatarBg}66`,
              boxShadow: `0 2px 8px ${avatarBg}40`,
              flexShrink: 0,
              fontSize: '0.85rem',
              fontWeight: 800,
            }}
          >
            {initials || ct.icon}
          </Avatar>

          {/* Agent + meta */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.4 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: '#0F172A',
                  letterSpacing: '-0.01em',
                  cursor: agent ? 'pointer' : 'default',
                  '&:hover': agent ? { color: '#6366F1' } : {},
                }}
                onClick={() => agent && navigate(`/reports/agent/${agent.id}`)}
              >
                {agent?.name || 'Unknown Agent'}
              </Typography>

              {/* Call type pill — padding/typography match CallFilterTabs chip scale */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  px: 1.1,
                  py: '3px',
                  borderRadius: '999px',
                  background: ct.bg,
                  border: `1px solid ${ct.border}`,
                }}
              >
                <Box sx={{ color: ct.color, display: 'flex', alignItems: 'center', lineHeight: 0 }}>{ct.icon}</Box>
                <Typography
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    color: ct.color,
                    letterSpacing: '0.01em',
                  }}
                >
                  {ct.label}
                </Typography>
              </Box>
            </Box>

            {/* Date */}
            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontWeight: 500 }}>
              {date}
            </Typography>
          </Box>

          {/* Duration pill — top right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.4,
              borderRadius: '8px',
              background: '#F8FAFC',
              border: '0.5px solid #E2E8F0',
              flexShrink: 0,
            }}
          >
            <AccessTimeIcon sx={{ fontSize: '0.75rem', color: '#94A3B8' }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', letterSpacing: '0.01em' }}>
              {durationFormatted || duration || '0:00'}
            </Typography>
          </Box>
        </Box>

        {/* ── Audio player ── */}
        {hasRecording && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              mb: 1.5,
              p: 1.25,
              borderRadius: '10px',
              background: '#F8FAFF',
              border: '0.5px solid #E8EEFF',
            }}
          >
            <AudioPlayer audioUrl={recordingUrl} duration={duration} sx={{ width: '100%', maxWidth: 320 }} />
          </Box>
        )}

        {/* ── Summary ── */}
        {hasSummary && (
          <Box
            sx={{
              mb: 1.5,
              px: 1.5,
              py: 1.25,
              borderRadius: '10px',
              background: '#FAFBFC',
              border: '0.5px solid #E2E8F0',
              borderLeft: '3px solid #6366F1',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <NotesIcon sx={{ fontSize: '0.75rem', color: '#6366F1' }} />
              <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#6366F1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Summary
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '0.78rem',
                color: '#475569',
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {summary}
            </Typography>
          </Box>
        )}

        {/* ── No summary placeholder ── */}
        {!hasSummary && !hasRecording && (
          <Box
            sx={{
              mb: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: '10px',
              background: '#F8FAFC',
              border: '0.5px dashed #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <PhoneIcon sx={{ fontSize: '0.9rem', color: '#CBD5E1' }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#CBD5E1', fontStyle: 'italic' }}>
              No recording or summary available
            </Typography>
          </Box>
        )}

        {/* ── Topics ── */}
        {topics && topics.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {topics.map((topic, i) => {
              const tc = getTopicTagChipColors(String(topic));
              return (
                <Chip
                  key={i}
                  label={topic}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.67rem',
                    fontWeight: 600,
                    bgcolor: tc.bgcolor,
                    color: tc.color,
                    border: `1px solid ${tc.borderColor}`,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ─────────────────────────── LeadCalls ─────────────────────────── */

const LeadCalls = () => {
  const { id: leadId } = useParams();
  const { calls, loading, fetchLeadCalls, callsPagination, getCallNotPickedStatus, triggerCallNotPicked } = useLeadDetails();
  const [loadingMore, setLoadingMore] = useState(false);
  const [callNotPickedLoading, setCallNotPickedLoading] = useState(false);
  const [callNotPickedAlreadySent, setCallNotPickedAlreadySent] = useState(false);
  const [callFilterTab, setCallFilterTab] = useState('all');
  const loadMoreLockRef = useRef(false);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    getCallNotPickedStatus(leadId)
      .then((data) => { if (!cancelled && data?.already_sent) setCallNotPickedAlreadySent(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leadId, getCallNotPickedStatus]);

  useEffect(() => {
    setCallFilterTab('all');
  }, [leadId]);

  const handleLoadMore = useCallback(async () => {
    if (!leadId || loadMoreLockRef.current || !callsPagination?.hasNext) return;
    loadMoreLockRef.current = true;
    setLoadingMore(true);
    try {
      await fetchLeadCalls(leadId, {
        page: callsPagination.currentPage + 1,
        page_size: callsPagination.pageSize,
      });
    } catch (e) {
      console.error('Error loading more calls:', e);
    } finally {
      setLoadingMore(false);
      loadMoreLockRef.current = false;
    }
  }, [leadId, callsPagination, fetchLeadCalls]);

  const handleTriggerCallNotPicked = async () => {
    if (!leadId) return;
    setCallNotPickedLoading(true);
    try {
      await triggerCallNotPicked(leadId);
      setCallNotPickedAlreadySent(true);
    } catch (e) {
      console.error('Error triggering call not picked:', e);
    } finally {
      setCallNotPickedLoading(false);
    }
  };

  const formattedCalls = useMemo(
    () =>
      (calls || []).map((call) => ({
        id: call.id,
        date: call.date || 'Unknown Date',
        duration: call.recording_length || '0:00',
        durationFormatted: call.recording_length_formatted || null,
        recordingUrl: call.recording_url || null,
        summary: call.general_insights?.summary || call.summary || 'No summary available',
        topics: call.general_insights?.topics_discussed || [],
        agent: call.agent || null,
        callType: call.call_type,
        status: call.status,
      })),
    [calls],
  );

  const tabCounts = useMemo(() => {
    const counts = { all: formattedCalls.length, incoming: 0, outgoing: 0, missed: 0, not_connected: 0 };
    formattedCalls.forEach((c) => {
      CALL_FILTER_TABS.forEach((tab) => {
        if (tab.id === 'all') return;
        if (callMatchesFilterTab(c, tab.id)) counts[tab.id] += 1;
      });
    });
    return counts;
  }, [formattedCalls]);

  const displayedCalls = useMemo(
    () => formattedCalls.filter((c) => callMatchesFilterTab(c, callFilterTab)),
    [formattedCalls, callFilterTab],
  );

  const callsScrollEnabled =
    Boolean(leadId) &&
    !loading.calls &&
    formattedCalls.length > 0 &&
    Boolean(callsPagination?.hasNext) &&
    (callFilterTab === 'all' || displayedCalls.length > 0);

  const loadMoreSentinelRef = useLeadTabInfiniteScroll({
    enabled: callsScrollEnabled,
    hasNext: Boolean(callsPagination?.hasNext),
    isBusy: loading.calls || loadingMore,
    onLoadMore: handleLoadMore,
    watchKey: `${callsPagination?.currentPage ?? 0}-${formattedCalls.length}`,
  });

  return (
    <Box sx={{ px: 2.5, py: 2.5 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0F172A', letterSpacing: '-0.02em' }}>
            Call History
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontWeight: 500 }}>
            {formattedCalls.length} call{formattedCalls.length !== 1 ? 's' : ''} on this page
            {callFilterTab !== 'all' && displayedCalls.length !== formattedCalls.length
              ? ` · ${displayedCalls.length} match filter`
              : ''}
          </Typography>
        </Box>

        <Tooltip
          title={
            callNotPickedAlreadySent
              ? 'Call not picked message already sent on WhatsApp'
              : 'Send "call not picked" WhatsApp message to lead'
          }
        >
          <span style={{ marginLeft: 'auto' }}>
            <Button
              size="small"
              startIcon={<WhatsAppIcon sx={{ fontSize: '0.95rem !important' }} />}
              onClick={handleTriggerCallNotPicked}
              disabled={!leadId || callNotPickedLoading || callNotPickedAlreadySent}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: '10px',
                px: 1.75,
                py: 0.75,
                border: '0.5px solid',
                borderColor: callNotPickedAlreadySent ? '#E2E8F0' : '#25D36640',
                color: callNotPickedAlreadySent ? '#94A3B8' : '#25D366',
                background: callNotPickedAlreadySent ? 'transparent' : '#25D36610',
                '&:hover:not(:disabled)': {
                  background: '#25D36618',
                  borderColor: '#25D36660',
                },
                '&.Mui-disabled': { opacity: 0.5 },
              }}
            >
              {callNotPickedLoading
                ? 'Sending…'
                : callNotPickedAlreadySent
                ? 'Message sent'
                : 'Not picked'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ── Filter tabs (client-side) ── */}
      {formattedCalls.length > 0 && (
        <CallFilterTabs activeTab={callFilterTab} onChange={setCallFilterTab} counts={tabCounts} />
      )}

      {/* ── Loading ── */}
      {loading.calls && <LeadTabPanelLoader />}

      {/* ── Empty ── */}
      {!loading.calls && formattedCalls.length === 0 && (
        <EmptyCallCard
          title="No call records found"
          description="Start tracking communication by logging your first call with this lead."
          compact
        />
      )}

      {!loading.calls && formattedCalls.length > 0 && displayedCalls.length === 0 && (
        <EmptyCallCard
          title="No calls in this category"
          description="Try another filter or scroll to load more — counts use disposition (Missed, Not connected) and direction (Incoming, Outgoing)."
          compact
        />
      )}

      {/* ── List ── */}
      {!loading.calls &&
        displayedCalls.map((call, i) => (
          <CallCard key={call.id || i} {...call} index={i} onViewDetails={() => {}} />
        ))}

      {!loading.calls &&
        callsPagination.hasNext &&
        (callFilterTab === 'all' || displayedCalls.length > 0) && (
          <>
            <Box
              ref={loadMoreSentinelRef}
              sx={{ height: 4, minHeight: 4, flexShrink: 0 }}
              aria-hidden
            />
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <LeadTabInlineProgress sx={{ mr: 0 }} />
              </Box>
            )}
          </>
        )}

      {/* ── Pagination info ── */}
      {!loading.calls && formattedCalls.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#CBD5E1', fontWeight: 500 }}>
            {displayedCalls.length} of {formattedCalls.length} on this page
            {callsPagination.totalCount != null ? ` · ${callsPagination.totalCount} total` : ''}
            {callFilterTab !== 'all' ? ' · filtered' : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LeadCalls;
